import { NotFoundError } from "@/compat/next/navigation";
import type { BlogPost, Category } from "@/lib/blog";
import type { ContentType } from "@/lib/contentSections";
import type {
  PortfolioCaseStudy,
  PortfolioCategory,
  PortfolioCompany,
  PortfolioIndustry,
} from "@/lib/portfolio";

const inflightGets = new Map<string, Promise<unknown>>();

async function getJson<T>(url: string): Promise<T> {
  const existing = inflightGets.get(url) as Promise<T> | undefined;
  if (existing) return existing;

  const request = (async () => {
    const res = await fetch(url);
    if (res.status === 404) throw new NotFoundError();
    if (!res.ok) throw new Error(`Request failed: ${res.status}`);
    return (await res.json()) as T;
  })().finally(() => {
    inflightGets.delete(url);
  });

  inflightGets.set(url, request);
  return request;
}

export const contentApi = {
  postsIndex: (type: ContentType, page: number) =>
    getJson<{
      posts: Array<Omit<BlogPost, "content"> & { content?: string }>;
      totalPages: number;
      categories: Category[];
      tags: string[];
    }>(`/api/content/posts?type=${encodeURIComponent(type)}&page=${page}`),

  /** Warm the blog index API before navigation. */
  prefetchPostsIndex: (type: ContentType = "blog", page = 1) => {
    void contentApi.postsIndex(type, page).catch(() => undefined);
  },

  post: (type: ContentType, slug: string) =>
    getJson<BlogPost>(`/api/content/posts/${encodeURIComponent(type)}/${encodeURIComponent(slug)}`),

  postsByCategory: (type: ContentType, slug: string) =>
    getJson<{ category: Category; posts: BlogPost[]; categories: Category[] }>(
      `/api/content/posts-by-category?type=${encodeURIComponent(type)}&slug=${encodeURIComponent(slug)}`,
    ),

  postsByTag: (slug: string) =>
    getJson<{ posts: BlogPost[] }>(`/api/content/posts-by-tag?slug=${encodeURIComponent(slug)}`),

  blogCategory: (slug: string) =>
    getJson<{ category: Category; posts: BlogPost[]; categories: Category[] }>(
      `/api/content/blog-category/${encodeURIComponent(slug)}`,
    ),

  portfolioIndex: () =>
    getJson<{
      caseStudies: PortfolioCaseStudy[];
      categories: PortfolioCategory[];
      industries: PortfolioIndustry[];
      companies: PortfolioCompany[];
    }>("/api/content/portfolio"),

  portfolioBySlug: (slug: string) =>
    getJson<PortfolioCaseStudy>(`/api/content/portfolio/${encodeURIComponent(slug)}`),

  portfolioCategory: (slug: string) =>
    getJson<{
      category: PortfolioCategory;
      projects: PortfolioCaseStudy[];
      categories: PortfolioCategory[];
    }>(`/api/content/portfolio-category/${encodeURIComponent(slug)}`),

  portfolioIndustry: (slug: string) =>
    getJson<{
      industry: PortfolioIndustry;
      projects: PortfolioCaseStudy[];
      industries: PortfolioIndustry[];
    }>(`/api/content/portfolio-industry/${encodeURIComponent(slug)}`),

  portfolioCompany: (slug: string) =>
    getJson<{
      company: PortfolioCompany;
      projects: PortfolioCaseStudy[];
    }>(`/api/content/portfolio-company/${encodeURIComponent(slug)}`),

  adminBootstrap: () =>
    getJson<{
      projects: PortfolioCaseStudy[];
      categories: PortfolioCategory[];
      industries: PortfolioIndustry[];
      adminApiAvailable: boolean;
    }>("/api/content/admin"),
};
